import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Theaters as ShowTimeIcon,
  People as PeopleIcon,
  CheckCircle as AdmitIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  ContentCopy as CopyIcon,
  History as HistoryIcon,
  VpnKey as RegenerateIcon,
  CalendarMonth as CalendarIcon,
  Schedule as ScheduleIcon,
  Bolt as BoltIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import io from 'socket.io-client';
import Header from '../Header';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const ManagementContainer = styled(Box)(() => ({
  backgroundColor: '#121212',
  minHeight: '100vh',
  padding: '24px',
}));

const StyledCard = styled(Card)(() => ({
  backgroundColor: '#1A1A1A',
  border: '1px solid #333',
  borderRadius: '12px',
  marginBottom: '24px',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#FF6B9D',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(255, 107, 157, 0.1)',
  },
}));

const ActionButton = styled(Button)(() => ({
  backgroundColor: '#FF6B9D',
  color: '#fff',
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '8px 16px',
  '&:hover': {
    backgroundColor: '#E91E63',
  },
}));

const inputStyles = {
  '& .MuiOutlinedInput-root': {
    color: '#fff',
    backgroundColor: '#121212',
    '& fieldset': { borderColor: '#333' },
    '&:hover fieldset': { borderColor: '#FF6B9D' },
    '&.Mui-focused fieldset': { borderColor: '#FF6B9D' },
  },
  '& .MuiInputLabel-root': {
    color: '#ccc',
    '&.Mui-focused': { color: '#FF6B9D' },
  },
};

const formatDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  return dt.toLocaleString();
};

const getStatusColor = (status) => {
  switch (status) {
    case 'new': return '#9E9E9E';
    case 'scheduled': return '#2196F3';
    case 'in_progress': return '#4CAF50';
    case 'ended': return '#607D8B';
    case 'deleted': return '#F44336';
    case 'no_show': return '#FF9800';
    default: return '#9E9E9E';
  }
};

const ShowTimeManagement = ({ embedded = false }) => {
  const [showTimes, setShowTimes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [selectedShowTime, setSelectedShowTime] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const socketRef = useRef(null);
  const selectedShowTimeRef = useRef(null);
  const participantsOpenRef = useRef(false);
  const historyOpenRef = useRef(false);
  const historyShowTimeRef = useRef(null);

  const [startNowMode, setStartNowMode] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    start_at: '',
    end_at: '',
    is_private: false,
    access_type: 'code',
    join_code: '',
    ticket_price: '',
    require_admin_admit: false,
  });

  const getDefaultEnd = (start) => {
    if (!start) return '';
    const s = new Date(start);
    s.setHours(s.getHours() + 4);
    return s.toISOString().slice(0, 16);
  };

  const toDatePart = (dtStr) => {
    if (!dtStr) return '';
    const d = new Date(dtStr);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  };
  const toTimePart = (dtStr) => {
    if (!dtStr) return '';
    const d = new Date(dtStr);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };
  const combineDateTime = (datePart, timePart) => {
    if (!datePart || !timePart) return '';
    return `${datePart}T${timePart}`;
  };

  const now = new Date();
  const minDate = now.toISOString().slice(0, 10);
  const minTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const getMinEndDate = () => toDatePart(formData.start_at) || minDate;
  const getMinEndTime = () => {
    const startDate = toDatePart(formData.start_at);
    const endDate = toDatePart(formData.end_at);
    if (startDate && endDate && startDate === endDate && formData.start_at) {
      const d = new Date(formData.start_at);
      d.setMinutes(d.getMinutes() + 1);
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    }
    return '00:00';
  };

  const resetForm = () => {
    const now = new Date();
    const start = now.toISOString().slice(0, 16);
    setStartNowMode(false);
    setFormData({
      name: '',
      start_at: start,
      end_at: getDefaultEnd(start),
      is_private: false,
      access_type: 'code',
      join_code: '',
      ticket_price: '',
      require_admin_admit: false,
    });
    setEditing(null);
  };

  const fetchShowTimes = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/api/show-times`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setShowTimes(data.showTimes || []);
      setError(null);
    } catch (err) {
      setError(err.message);
      enqueueSnackbar('Failed to fetch show times', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  const fetchShowTimesSilent = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/show-times`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setShowTimes(data.showTimes || []);
    } catch (_) { /* silent */ }
  }, []);

  const refreshParticipantsSilent = useCallback(async (showTime) => {
    if (!showTime) return;
    try {
      const res = await fetch(`${API_BASE}/api/show-times/${showTime.id}/participants`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      const data = await res.json();
      setParticipants(data.participants || []);
    } catch (_) { /* silent */ }
  }, []);

  const refreshHistorySilent = useCallback(async (st) => {
    if (!st) return;
    try {
      const res = await fetch(`${API_BASE}/api/show-times/${st.id}/history`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      const data = await res.json();
      if (res.ok) setHistoryData(data);
    } catch (_) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchShowTimes();
  }, [fetchShowTimes]);

  useEffect(() => {
    const socket = io(API_BASE, { transports: ['websocket'], reconnection: true });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('admin:join-showtime');
    });

    socket.on('showtime:updated', () => {
      fetchShowTimesSilent();
      if (participantsOpenRef.current && selectedShowTimeRef.current) {
        refreshParticipantsSilent(selectedShowTimeRef.current);
      }
      if (historyOpenRef.current && historyShowTimeRef.current) {
        refreshHistorySilent(historyShowTimeRef.current);
      }
    });

    socket.on('showtime:participants-updated', ({ showTimeId }) => {
      fetchShowTimesSilent();
      if (
        participantsOpenRef.current &&
        selectedShowTimeRef.current &&
        selectedShowTimeRef.current.id === showTimeId
      ) {
        refreshParticipantsSilent(selectedShowTimeRef.current);
      }
      if (historyOpenRef.current && historyShowTimeRef.current) {
        refreshHistorySilent(historyShowTimeRef.current);
      }
    });

    socket.on('showtime:history-updated', () => {
      if (historyOpenRef.current && historyShowTimeRef.current) {
        refreshHistorySilent(historyShowTimeRef.current);
      }
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.emit('admin:leave-showtime');
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [fetchShowTimesSilent, refreshParticipantsSilent, refreshHistorySilent]);

  const handleCreate = () => {
    resetForm();
    setEditOpen(false);
    const now = new Date();
    const start = now.toISOString().slice(0, 16);
    setFormData((prev) => ({ ...prev, start_at: start, end_at: getDefaultEnd(start) }));
    setCreateOpen(true);
  };

  const handleEdit = (st) => {
    setCreateOpen(false);
    setEditing(st);
    setFormData({
      name: st.name,
      start_at: st.start_at ? st.start_at.slice(0, 16) : '',
      end_at: st.end_at ? st.end_at.slice(0, 16) : '',
      is_private: !!st.is_private,
      access_type: st.access_type || 'code',
      join_code: st.join_code || '',
      ticket_price: st.ticket_price ?? '',
      require_admin_admit: !!st.require_admin_admit,
    });
    setEditOpen(true);
  };

  const handleSave = async (andStart = false) => {
    if (!formData.name?.trim() || !formData.start_at || !formData.end_at) {
      enqueueSnackbar('Name, start and end time are required', { variant: 'error' });
      return;
    }
    let startIso, endIso;
    if (startNowMode && andStart) {
      const nowDt = new Date();
      startIso = nowDt.toISOString();
      const endDt = new Date(nowDt);
      endDt.setHours(endDt.getHours() + 4);
      endIso = endDt.toISOString();
    } else {
      startIso = new Date(formData.start_at).toISOString();
      endIso = new Date(formData.end_at).toISOString();
    }
    const now = new Date();
    const startDt = new Date(startIso);
    const endDt = new Date(endIso);
    if (!editing && !startNowMode) {
      if (startDt < now && !andStart) {
        enqueueSnackbar('Start time cannot be in the past', { variant: 'error' });
        return;
      }
      if (endDt < now) {
        enqueueSnackbar('End time cannot be in the past', { variant: 'error' });
        return;
      }
    }
    if (endDt <= startDt) {
      enqueueSnackbar('End time must be after start time', { variant: 'error' });
      return;
    }
    const payload = {
      name: formData.name.trim(),
      start_at: startIso,
      end_at: endIso,
      is_private: formData.is_private,
      access_type: formData.access_type,
      join_code: formData.is_private ? (formData.join_code || undefined) : null,
      ticket_price: formData.is_private && formData.access_type === 'payment' ? parseFloat(formData.ticket_price) || null : null,
      require_admin_admit: formData.is_private && formData.require_admin_admit,
    };
    try {
      if (editing) {
        const res = await fetch(`${API_BASE}/api/show-times/${editing.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
          },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!res.ok) {
          enqueueSnackbar(data.error || 'Update failed', { variant: 'error' });
          return;
        }
        enqueueSnackbar('Show time updated', { variant: 'success' });
        setEditOpen(false);
      } else {
        const res = await fetch(`${API_BASE}/api/show-times`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
          },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!res.ok) {
          enqueueSnackbar(data.error || 'Create failed', { variant: 'error' });
          return;
        }
        enqueueSnackbar('Show time created', { variant: 'success' });
        setCreateOpen(false);
        if (andStart && data.id) {
          try {
            const sr = await fetch(`${API_BASE}/api/show-times/${data.id}/start`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
            });
            if (sr.ok) enqueueSnackbar('Show time started', { variant: 'success' });
            else {
              const errData = await sr.json();
              enqueueSnackbar(errData.error || 'Start failed', { variant: 'error' });
            }
          } catch (e) {
            enqueueSnackbar('Start failed', { variant: 'error' });
          }
        }
      }
      fetchShowTimes();
      if (andStart && editing) {
        const sr = await fetch(`${API_BASE}/api/show-times/${editing.id}/start`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
        });
        if (sr.ok) enqueueSnackbar('Show time started', { variant: 'success' });
        else {
          const d = await sr.json();
          enqueueSnackbar(d.error || 'Start failed', { variant: 'error' });
        }
        fetchShowTimes();
      }
    } catch (err) {
      enqueueSnackbar(err.message || 'Failed', { variant: 'error' });
    }
  };

  const handleStartNow = async (st) => {
    try {
      const res = await fetch(`${API_BASE}/api/show-times/${st.id}/start`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      const data = await res.json();
      if (!res.ok) {
        enqueueSnackbar(data.error || 'Start failed', { variant: 'error' });
        return;
      }
      enqueueSnackbar('Show time started', { variant: 'success' });
      fetchShowTimes();
    } catch (err) {
      enqueueSnackbar(err.message || 'Failed', { variant: 'error' });
    }
  };

  const handleEnd = async (st) => {
    try {
      const res = await fetch(`${API_BASE}/api/show-times/${st.id}/end`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      if (!res.ok) throw new Error('End failed');
      enqueueSnackbar('Show time ended', { variant: 'success' });
      fetchShowTimes();
    } catch (err) {
      enqueueSnackbar(err.message || 'Failed', { variant: 'error' });
    }
  };

  const handleNoShow = async (st) => {
    try {
      const res = await fetch(`${API_BASE}/api/show-times/${st.id}/no-show`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      if (!res.ok) throw new Error('Failed');
      enqueueSnackbar('Marked as No Show', { variant: 'success' });
      fetchShowTimes();
    } catch (err) {
      enqueueSnackbar(err.message || 'Failed', { variant: 'error' });
    }
  };

  const handleDelete = async (st) => {
    if (!window.confirm('Delete this show time?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/show-times/${st.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      if (!res.ok) throw new Error('Delete failed');
      enqueueSnackbar('Show time deleted', { variant: 'success' });
      fetchShowTimes();
    } catch (err) {
      enqueueSnackbar(err.message || 'Failed', { variant: 'error' });
    }
  };

  const handleRegenerateCode = async (st) => {
    try {
      const res = await fetch(`${API_BASE}/api/show-times/${st.id}/regenerate-code`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error('Failed');
      enqueueSnackbar(`New code: ${data.join_code}`, { variant: 'success' });
      fetchShowTimes();
    } catch (err) {
      enqueueSnackbar(err.message || 'Failed', { variant: 'error' });
    }
  };

  const openParticipants = async (st) => {
    setSelectedShowTime(st);
    selectedShowTimeRef.current = st;
    setParticipantsOpen(true);
    participantsOpenRef.current = true;
    setLoadingParticipants(true);
    try {
      const res = await fetch(`${API_BASE}/api/show-times/${st.id}/participants`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      const data = await res.json();
      setParticipants(data.participants || []);
    } catch (err) {
      enqueueSnackbar('Failed to load participants', { variant: 'error' });
    } finally {
      setLoadingParticipants(false);
    }
  };

  const handleAdmit = async (participantId) => {
    if (!selectedShowTime) return;
    try {
      const res = await fetch(
        `${API_BASE}/api/show-times/${selectedShowTime.id}/participants/${participantId}/admit`,
        { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } }
      );
      if (!res.ok) throw new Error('Failed');
      enqueueSnackbar('User admitted', { variant: 'success' });
      openParticipants(selectedShowTime);
    } catch (err) {
      enqueueSnackbar(err.message || 'Failed', { variant: 'error' });
    }
  };

  const handleReject = async (participantId) => {
    if (!selectedShowTime) return;
    try {
      const res = await fetch(
        `${API_BASE}/api/show-times/${selectedShowTime.id}/participants/${participantId}/reject`,
        { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } }
      );
      if (!res.ok) throw new Error('Failed');
      enqueueSnackbar('User rejected', { variant: 'info' });
      openParticipants(selectedShowTime);
    } catch (err) {
      enqueueSnackbar(err.message || 'Failed', { variant: 'error' });
    }
  };

  const openHistory = async (st) => {
    setHistoryOpen(true);
    historyOpenRef.current = true;
    historyShowTimeRef.current = st;
    setHistoryData(null);
    setLoadingHistory(true);
    try {
      const res = await fetch(`${API_BASE}/api/show-times/${st.id}/history`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load history');
      setHistoryData(data);
    } catch (err) {
      enqueueSnackbar(err.message || 'Failed to load history', { variant: 'error' });
    } finally {
      setLoadingHistory(false);
    }
  };

  const getDisplayName = (row) => {
    if (row.fname) return `${row.fname} ${row.lname || ''}`.trim();
    if (row.guest_name) return row.guest_name;
    if (row.display_name) return row.display_name;
    return 'Unknown';
  };

  const copyCode = (code) => {
    if (code) {
      navigator.clipboard.writeText(code);
      enqueueSnackbar('Code copied', { variant: 'success' });
    }
  };

  const handleFormClose = () => {
    setCreateOpen(false);
    setEditOpen(false);
    setEditing(null);
  };

  const FormDialogContent = (
    <Dialog open={createOpen || editOpen} onClose={handleFormClose} maxWidth="md" fullWidth PaperProps={{ sx: { backgroundColor: '#1A1A1A' } }}>
      <DialogTitle sx={{ color: '#fff', borderBottom: '1px solid #333', py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ShowTimeIcon sx={{ mr: 2, color: '#FF6B9D' }} />
          {editing ? 'Edit Show Time' : 'Create Show Time'}
        </Box>
      </DialogTitle>
      <form onSubmit={(e) => e.preventDefault()} noValidate>
        <DialogContent sx={{ pt: 3, backgroundColor: '#1A1A1A' }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField fullWidth label="Name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required sx={inputStyles} />
            </Grid>
            {!editing && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={startNowMode}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setStartNowMode(checked);
                        if (checked) {
                          const nowStr = new Date().toISOString().slice(0, 16);
                          setFormData((prev) => ({ ...prev, start_at: nowStr, end_at: getDefaultEnd(nowStr) }));
                        }
                      }}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FF6B9D' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FF6B9D' } }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BoltIcon sx={{ color: startNowMode ? '#FF6B9D' : '#666', fontSize: 20 }} />
                      <Typography sx={{ color: startNowMode ? '#FF6B9D' : '#ccc', fontWeight: startNowMode ? 600 : 400 }}>
                        Start Now (4 hr session)
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CalendarIcon sx={{ color: '#FF6B9D', fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ color: '#B0B0B0' }}>Start Date & Time</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField type="date" label="Date" value={toDatePart(formData.start_at)} InputLabelProps={{ shrink: true }} inputProps={{ min: editing ? undefined : minDate }} disabled={startNowMode} sx={{ ...inputStyles, minWidth: 180 }} onChange={(e) => { const next = combineDateTime(e.target.value, toTimePart(formData.start_at)); setFormData({ ...formData, start_at: next, end_at: formData.end_at || getDefaultEnd(next) }); }} />
                <TextField type="time" label="Time" value={toTimePart(formData.start_at)} InputLabelProps={{ shrink: true }} inputProps={{ min: !editing && toDatePart(formData.start_at) === minDate ? minTime : undefined }} disabled={startNowMode} sx={{ ...inputStyles, minWidth: 140 }} onChange={(e) => { const next = combineDateTime(toDatePart(formData.start_at), e.target.value); setFormData({ ...formData, start_at: next, end_at: formData.end_at || getDefaultEnd(next) }); }} />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ScheduleIcon sx={{ color: '#FF6B9D', fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ color: startNowMode ? '#555' : '#B0B0B0' }}>
                  End Date & Time {startNowMode ? '(auto: start + 4 hrs)' : '(must be after start)'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField type="date" label="Date" value={toDatePart(formData.end_at)} InputLabelProps={{ shrink: true }} inputProps={{ min: editing ? (toDatePart(formData.start_at) || undefined) : getMinEndDate() }} disabled={startNowMode} sx={{ ...inputStyles, minWidth: 180 }} onChange={(e) => { const next = combineDateTime(e.target.value, toTimePart(formData.end_at)); if (next && formData.start_at && next <= formData.start_at) { setFormData({ ...formData, end_at: getDefaultEnd(formData.start_at) }); } else { setFormData({ ...formData, end_at: next }); } }} />
                <TextField type="time" label="Time" value={toTimePart(formData.end_at)} InputLabelProps={{ shrink: true }} inputProps={{ min: getMinEndTime() }} disabled={startNowMode} sx={{ ...inputStyles, minWidth: 140 }} onChange={(e) => { const next = combineDateTime(toDatePart(formData.end_at), e.target.value); if (next && formData.start_at && next <= formData.start_at) { enqueueSnackbar('End time must be after start time', { variant: 'warning' }); return; } setFormData({ ...formData, end_at: next }); }} />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={formData.is_private} onChange={(e) => setFormData({ ...formData, is_private: e.target.checked })} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FF6B9D' } }} />}
                label={<Typography sx={{ color: '#ccc' }}>Private</Typography>}
              />
            </Grid>
            {formData.is_private && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={<Switch checked={formData.access_type === 'payment'} onChange={(e) => setFormData({ ...formData, access_type: e.target.checked ? 'payment' : 'code' })} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FF6B9D' } }} />}
                    label={<Typography sx={{ color: '#ccc' }}>Payment required</Typography>}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={<Switch checked={formData.require_admin_admit} onChange={(e) => setFormData({ ...formData, require_admin_admit: e.target.checked })} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FF6B9D' } }} />}
                    label={<Typography sx={{ color: '#ccc' }}>Admit by Admin</Typography>}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Join code (or leave empty to auto-generate)" value={formData.join_code} onChange={(e) => setFormData({ ...formData, join_code: e.target.value })} sx={inputStyles} />
                </Grid>
                {formData.access_type === 'payment' && (
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth type="number" label="Ticket price" value={formData.ticket_price} onChange={(e) => setFormData({ ...formData, ticket_price: e.target.value })} InputProps={{ inputProps: { min: 0, step: 0.01 } }} sx={inputStyles} />
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #333' }}>
          <Button type="button" onClick={handleFormClose} sx={{ color: '#aaa' }}>Cancel</Button>
          {!editing && startNowMode && (
            <ActionButton type="button" onClick={() => handleSave(true)} startIcon={<BoltIcon />}>Save & Start Now</ActionButton>
          )}
          {!editing && !startNowMode && (
            <ActionButton type="button" onClick={() => handleSave(true)} startIcon={<PlayIcon />}>Save and Start Now</ActionButton>
          )}
          {editing && ['new', 'scheduled'].includes(editing.status) && (
            <ActionButton type="button" onClick={() => handleSave(true)} startIcon={<PlayIcon />}>Save and Start Now</ActionButton>
          )}
          <ActionButton type="button" onClick={() => handleSave(false)} disabled={!editing && startNowMode}>Save</ActionButton>
        </DialogActions>
      </form>
    </Dialog>
  );

  return (
    <>
      {!embedded && <Header />}
      <ManagementContainer
        sx={
          embedded
            ? {
                minHeight: 'auto',
                padding: 0,
                backgroundColor: 'transparent',
              }
            : undefined
        }
      >
        <Box sx={{ mb: embedded ? 2 : 4 }}>
          {!embedded && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ShowTimeIcon sx={{ fontSize: 32, color: '#FF6B9D', mr: 2 }} />
                <Typography variant="h4" component="h1" sx={{ color: '#fff', fontWeight: 'bold' }}>Show Time Management</Typography>
              </Box>
              <Typography variant="body1" sx={{ color: '#B0B0B0', mb: 3 }}>Manage show times with private/public access, lobby admission, and scheduling</Typography>
            </>
          )}
          <Box sx={{ display: 'flex', gap: 2, mb: embedded ? 2 : 3 }}>
            <ActionButton type="button" variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>Create Show Time</ActionButton>
            <Button type="button" variant="outlined" startIcon={<RefreshIcon />} onClick={fetchShowTimes} sx={{ color: '#FF6B9D', borderColor: '#FF6B9D' }}>Refresh</Button>
          </Box>
        </Box>

        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: '#FF6B9D' }} /></Box>}
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        {!loading && showTimes.length === 0 && <Alert severity="info" sx={{ mb: 3 }}>No show times found. Create your first show time.</Alert>}

        <Grid container spacing={3}>
          {showTimes.map((st) => (
            <Grid item xs={12} md={6} lg={4} key={st.id}>
              <StyledCard>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>{st.name}</Typography>
                    <Chip label={st.status} size="small" sx={{ backgroundColor: `${getStatusColor(st.status)}20`, color: getStatusColor(st.status), border: `1px solid ${getStatusColor(st.status)}` }} />
                  </Box>
                  <Typography variant="body2" sx={{ color: '#B0B0B0', mb: 0.5 }}>Start: {formatDate(st.start_at)}</Typography>
                  <Typography variant="body2" sx={{ color: '#B0B0B0', mb: 1 }}>End: {formatDate(st.end_at)}</Typography>
                  {!!st.is_private && st.join_code && (
                    <Typography variant="body2" sx={{ color: '#B0B0B0', mb: 1 }}>Code: {st.join_code}</Typography>
                  )}
                  <Typography variant="body2" sx={{ color: '#B0B0B0', mb: 1 }}>Admitted: {st.admitted_count ?? 0} | Pending: {st.pending_count ?? 0}</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    <Tooltip title="Edit"><IconButton type="button" size="small" onClick={() => handleEdit(st)} sx={{ color: '#4CAF50' }}><EditIcon /></IconButton></Tooltip>
                    <Tooltip title="Participants / Admit"><IconButton type="button" size="small" onClick={() => openParticipants(st)} sx={{ color: '#2196F3' }}><PeopleIcon /></IconButton></Tooltip>
                    <Tooltip title="History"><IconButton type="button" size="small" onClick={() => openHistory(st)} sx={{ color: '#9E9E9E' }}><HistoryIcon /></IconButton></Tooltip>
                    {!!st.is_private && st.join_code && <Tooltip title="Copy code"><IconButton type="button" size="small" onClick={() => copyCode(st.join_code)} sx={{ color: '#FF9800' }}><CopyIcon /></IconButton></Tooltip>}
                    {!!st.is_private && <Tooltip title="Regenerate code"><IconButton type="button" size="small" onClick={() => handleRegenerateCode(st)} sx={{ color: '#9C27B0' }}><RegenerateIcon /></IconButton></Tooltip>}
                    {st.status === 'new' && <Tooltip title="Start now"><IconButton type="button" size="small" onClick={() => handleStartNow(st)} sx={{ color: '#4CAF50' }}><PlayIcon /></IconButton></Tooltip>}
                    {st.status === 'in_progress' && <Tooltip title="End"><IconButton type="button" size="small" onClick={() => handleEnd(st)} sx={{ color: '#F44336' }}><StopIcon /></IconButton></Tooltip>}
                    {['new', 'scheduled'].includes(st.status) && <Tooltip title="No Show"><IconButton type="button" size="small" onClick={() => handleNoShow(st)} sx={{ color: '#FF9800' }}>NS</IconButton></Tooltip>}
                    {['new', 'scheduled', 'in_progress', 'ended', 'no_show'].includes(st.status) && <Tooltip title="Delete"><IconButton type="button" size="small" onClick={() => handleDelete(st)} sx={{ color: '#F44336' }}><DeleteIcon /></IconButton></Tooltip>}
                  </Box>
                </CardContent>
              </StyledCard>
            </Grid>
          ))}
        </Grid>

        {FormDialogContent}

        <Dialog open={participantsOpen} onClose={() => { setParticipantsOpen(false); participantsOpenRef.current = false; selectedShowTimeRef.current = null; }} maxWidth="md" fullWidth PaperProps={{ sx: { backgroundColor: '#1A1A1A' } }}>
          <DialogTitle sx={{ color: '#fff', borderBottom: '1px solid #333' }}>
            Participants {selectedShowTime ? `— ${selectedShowTime.name}` : ''}
          </DialogTitle>
          <DialogContent>
            {loadingParticipants ? <CircularProgress sx={{ color: '#FF6B9D' }} /> : (
              <TableContainer component={Paper} sx={{ backgroundColor: '#121212' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: '#FF6B9D' }}>Name</TableCell>
                      <TableCell sx={{ color: '#FF6B9D' }}>Email</TableCell>
                      <TableCell sx={{ color: '#FF6B9D' }}>Status</TableCell>
                      <TableCell sx={{ color: '#FF6B9D' }}>Joined</TableCell>
                      <TableCell sx={{ color: '#FF6B9D' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {participants.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell sx={{ color: '#fff' }}>{p.fname} {p.lname || p.guest_name || ''}</TableCell>
                        <TableCell sx={{ color: '#ccc' }}>{p.email || '-'}</TableCell>
                        <TableCell>
                          <Chip label={p.status} size="small" sx={{ backgroundColor: p.status === 'admitted' ? 'rgba(76,175,80,0.2)' : p.status === 'rejected' ? 'rgba(244,67,54,0.2)' : 'rgba(255,152,0,0.2)', color: p.status === 'admitted' ? '#4CAF50' : p.status === 'rejected' ? '#F44336' : '#FF9800' }} />
                        </TableCell>
                        <TableCell sx={{ color: '#ccc' }}>{formatDate(p.joined_at)}</TableCell>
                        <TableCell>
                          {p.status === 'pending' && selectedShowTime?.require_admin_admit && (
                            <>
                              <IconButton type="button" size="small" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleAdmit(p.id); }} sx={{ color: '#4CAF50' }}><AdmitIcon /></IconButton>
                              <IconButton type="button" size="small" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleReject(p.id); }} sx={{ color: '#F44336' }}>✕</IconButton>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={historyOpen} onClose={() => { setHistoryOpen(false); historyOpenRef.current = false; historyShowTimeRef.current = null; }} maxWidth="lg" fullWidth PaperProps={{ sx: { backgroundColor: '#1A1A1A', minHeight: '60vh' } }}>
          <DialogTitle sx={{ color: '#fff', borderBottom: '1px solid #333' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon sx={{ color: '#FF6B9D' }} />
              Session History {historyData?.showTime ? `— ${historyData.showTime.name}` : ''}
            </Box>
            {historyData?.showTime && (
              <Typography variant="body2" sx={{ color: '#888', mt: 0.5 }}>
                {formatDate(historyData.showTime.actual_start)} — {formatDate(historyData.showTime.actual_end)}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {loadingHistory && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: '#FF6B9D' }} /></Box>}
            {!loadingHistory && historyData && (() => {
              const { games } = historyData;
              const hasAny = games.quiz.length > 0 || games.higherOrLower.length > 0 || games.spinningWheel.length > 0 || games.buzzer.length > 0 || (games.beerillioner && games.beerillioner.length > 0);
              if (!hasAny) return <Typography sx={{ color: '#888', py: 4, textAlign: 'center' }}>No game activity in this session.</Typography>;
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {games.quiz.length > 0 && (
                    <Box>
                      <Typography variant="subtitle1" sx={{ color: '#FF6B9D', fontWeight: 600, mb: 1 }}>Quiz</Typography>
                      <TableContainer component={Paper} sx={{ backgroundColor: '#121212' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ color: '#FF6B9D' }}>Player</TableCell>
                              <TableCell sx={{ color: '#FF6B9D' }}>Score</TableCell>
                              <TableCell sx={{ color: '#FF6B9D' }}>Correct / Total</TableCell>
                              <TableCell sx={{ color: '#FF6B9D' }}>Status</TableCell>
                              <TableCell sx={{ color: '#FF6B9D' }}>Started</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {games.quiz.map((q) => (
                              <TableRow key={q.user_plays_master_id}>
                                <TableCell sx={{ color: '#fff' }}>{getDisplayName(q)}</TableCell>
                                <TableCell sx={{ color: '#4CAF50', fontWeight: 600 }}>{q.total_score}</TableCell>
                                <TableCell sx={{ color: '#ccc' }}>{q.correct_answers} / {q.total_questions}</TableCell>
                                <TableCell><Chip label={q.status} size="small" sx={{ backgroundColor: q.status === 'completed' ? 'rgba(76,175,80,0.2)' : 'rgba(255,152,0,0.2)', color: q.status === 'completed' ? '#4CAF50' : '#FF9800' }} /></TableCell>
                                <TableCell sx={{ color: '#ccc' }}>{formatDate(q.started_at)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}

                  {games.higherOrLower.length > 0 && (
                    <Box>
                      <Typography variant="subtitle1" sx={{ color: '#FF6B9D', fontWeight: 600, mb: 1 }}>Higher or Lower</Typography>
                      <TableContainer component={Paper} sx={{ backgroundColor: '#121212' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ color: '#FF6B9D' }}>Player</TableCell>
                              <TableCell sx={{ color: '#FF6B9D' }}>Score</TableCell>
                              <TableCell sx={{ color: '#FF6B9D' }}>Rounds</TableCell>
                              <TableCell sx={{ color: '#FF6B9D' }}>Status</TableCell>
                              <TableCell sx={{ color: '#FF6B9D' }}>Started</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {games.higherOrLower.map((h) => (
                              <TableRow key={h.session_id}>
                                <TableCell sx={{ color: '#fff' }}>{getDisplayName(h)}</TableCell>
                                <TableCell sx={{ color: '#4CAF50', fontWeight: 600 }}>{h.final_score}</TableCell>
                                <TableCell sx={{ color: '#ccc' }}>{h.total_rounds}</TableCell>
                                <TableCell><Chip label={h.game_status} size="small" sx={{ backgroundColor: h.game_status === 'completed' ? 'rgba(76,175,80,0.2)' : 'rgba(255,152,0,0.2)', color: h.game_status === 'completed' ? '#4CAF50' : '#FF9800' }} /></TableCell>
                                <TableCell sx={{ color: '#ccc' }}>{formatDate(h.started_at)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}

                  {games.spinningWheel.length > 0 && (
                    <Box>
                      <Typography variant="subtitle1" sx={{ color: '#FF6B9D', fontWeight: 600, mb: 1 }}>Spinning Wheel</Typography>
                      <TableContainer component={Paper} sx={{ backgroundColor: '#121212' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ color: '#FF6B9D' }}>Player</TableCell>
                              <TableCell sx={{ color: '#FF6B9D' }}>Points</TableCell>
                              <TableCell sx={{ color: '#FF6B9D' }}>Result</TableCell>
                              <TableCell sx={{ color: '#FF6B9D' }}>Time</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {games.spinningWheel.map((s) => (
                              <TableRow key={s.spin_id}>
                                <TableCell sx={{ color: '#fff' }}>{getDisplayName(s)}</TableCell>
                                <TableCell sx={{ color: '#4CAF50', fontWeight: 600 }}>{s.points_awarded}</TableCell>
                                <TableCell sx={{ color: '#ccc' }}>{s.spin_result || '-'}</TableCell>
                                <TableCell sx={{ color: '#ccc' }}>{formatDate(s.created_at)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}

                  {games.buzzer.length > 0 && (
                    <Box>
                      <Typography variant="subtitle1" sx={{ color: '#FF6B9D', fontWeight: 600, mb: 1 }}>Buzzer</Typography>
                      <TableContainer component={Paper} sx={{ backgroundColor: '#121212' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ color: '#FF6B9D' }}>Player</TableCell>
                              <TableCell sx={{ color: '#FF6B9D' }}>Winner?</TableCell>
                              <TableCell sx={{ color: '#FF6B9D' }}>Latency</TableCell>
                              <TableCell sx={{ color: '#FF6B9D' }}>Time</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {games.buzzer.map((b) => (
                              <TableRow key={b.attempt_id}>
                                <TableCell sx={{ color: '#fff' }}>{getDisplayName(b)}</TableCell>
                                <TableCell>{b.is_winner ? <Chip label="Winner" size="small" sx={{ backgroundColor: 'rgba(76,175,80,0.2)', color: '#4CAF50' }} /> : <Chip label="Attempt" size="small" sx={{ backgroundColor: 'rgba(158,158,158,0.2)', color: '#9E9E9E' }} />}</TableCell>
                                <TableCell sx={{ color: '#ccc' }}>{b.latency_ms ? `${b.latency_ms}ms` : '-'}</TableCell>
                                <TableCell sx={{ color: '#ccc' }}>{formatDate(b.created_at)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}

                  {games.beerillioner && games.beerillioner.length > 0 && (
                    <Box>
                      <Typography variant="subtitle1" sx={{ color: '#FFD700', fontWeight: 600, mb: 1 }}>Who Wants to Beer Millionaire</Typography>
                      {games.beerillioner.map((bs) => (
                        <Paper key={bs.id} sx={{ backgroundColor: '#121212', p: 2, mb: 2, border: '1px solid #333', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                            <Chip label={bs.game_name || 'Beerillioner'} size="small" sx={{ backgroundColor: 'rgba(255,215,0,0.15)', color: '#FFD700', fontWeight: 600 }} />
                            <Chip label={`Session #${bs.id}`} size="small" sx={{ backgroundColor: '#222', color: '#888' }} />
                            <Chip label={bs.session_mode === 'random' ? 'Random' : 'Pre-select'} size="small" sx={{ backgroundColor: bs.session_mode === 'random' ? 'rgba(230,81,0,0.2)' : 'rgba(255,215,0,0.1)', color: bs.session_mode === 'random' ? '#e65100' : '#FFD700' }} />
                            {bs.hot_seat_display_name && <Chip label={`Hot Seat: ${bs.hot_seat_display_name}`} size="small" sx={{ backgroundColor: 'rgba(184,134,11,0.2)', color: '#DAA520' }} />}
                            <Chip label={`Level ${bs.current_level || 0}`} size="small" sx={{ backgroundColor: 'rgba(74,103,65,0.3)', color: '#81c784' }} />
                            {bs.walk_away ? <Chip label="Walked Away" size="small" sx={{ backgroundColor: 'rgba(255,152,0,0.2)', color: '#FF9800' }} /> : null}
                            <Typography variant="caption" sx={{ color: '#888', ml: 'auto' }}>{formatDate(bs.created_at)}</Typography>
                          </Box>

                          {bs.fffRounds && bs.fffRounds.length > 0 && (
                            <Box sx={{ mb: 1.5 }}>
                              <Typography variant="body2" sx={{ color: '#64b5f6', fontWeight: 600, mb: 0.5 }}>Fastest Finger First</Typography>
                              {bs.fffRounds.map((round, ri) => (
                                <Box key={round.id || ri} sx={{ p: 1, bgcolor: 'rgba(13,27,42,0.5)', borderRadius: 1, mb: 0.5, border: '1px solid #1a2a3a' }}>
                                  <Typography variant="caption" sx={{ color: '#90caf9', fontWeight: 600 }}>R{round.round_number}: {round.question_text}</Typography>
                                  <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                                    {['A','B','C','D'].map(o => (
                                      <Typography key={o} variant="caption" sx={{ color: round.correct_answer === o ? '#4caf50' : '#888' }}>
                                        {o}: {round[`option_${o.toLowerCase()}`]}{round.correct_answer === o ? ' ✓' : ''}
                                      </Typography>
                                    ))}
                                  </Box>
                                  {round.answers && round.answers.length > 0 && (
                                    <Box sx={{ mt: 0.5 }}>
                                      <Typography variant="caption" sx={{ color: '#666' }}>Answers: </Typography>
                                      {round.answers.map((a, ai) => (
                                        <Chip key={a.id || ai} label={`${a.display_name}: ${a.answer}${a.is_correct ? ' ✓' : ' ✗'}`} size="small" sx={{ mr: 0.5, mt: 0.3, height: 20, fontSize: '0.65rem', backgroundColor: a.is_correct ? 'rgba(76,175,80,0.15)' : 'rgba(239,83,80,0.15)', color: a.is_correct ? '#4caf50' : '#ef5350' }} />
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          )}

                          {bs.mainGameAnswers && bs.mainGameAnswers.length > 0 && (
                            <Box>
                              <Typography variant="body2" sx={{ color: '#FFD700', fontWeight: 600, mb: 0.5 }}>Main Game — {bs.hot_seat_display_name || 'Unknown'}</Typography>
                              <TableContainer>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ color: '#FFD700', py: 0.5 }}>Level</TableCell>
                                      <TableCell sx={{ color: '#FFD700', py: 0.5 }}>Question</TableCell>
                                      <TableCell sx={{ color: '#FFD700', py: 0.5 }}>Correct</TableCell>
                                      <TableCell sx={{ color: '#FFD700', py: 0.5 }}>Answer</TableCell>
                                      <TableCell sx={{ color: '#FFD700', py: 0.5 }}>Result</TableCell>
                                      <TableCell sx={{ color: '#FFD700', py: 0.5 }}>Time</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {bs.mainGameAnswers.map((q, qi) => (
                                      <TableRow key={q.id || qi}>
                                        <TableCell sx={{ color: '#81c784', fontWeight: 600, py: 0.5 }}>{q.level}</TableCell>
                                        <TableCell sx={{ color: '#fff', py: 0.5, maxWidth: 300 }}><Typography variant="caption" noWrap>{q.question_text}</Typography></TableCell>
                                        <TableCell sx={{ color: '#4caf50', py: 0.5 }}>{q.correct_answer}</TableCell>
                                        <TableCell sx={{ color: q.is_correct ? '#4caf50' : '#ef5350', py: 0.5, fontWeight: 600 }}>{q.player_answer || '—'}</TableCell>
                                        <TableCell sx={{ py: 0.5 }}><Chip label={q.is_correct ? 'Correct' : 'Wrong'} size="small" sx={{ height: 20, fontSize: '0.65rem', backgroundColor: q.is_correct ? 'rgba(76,175,80,0.2)' : 'rgba(239,83,80,0.2)', color: q.is_correct ? '#4caf50' : '#ef5350' }} /></TableCell>
                                        <TableCell sx={{ color: '#888', py: 0.5 }}>{q.time_taken_seconds != null ? `${q.time_taken_seconds}s` : '—'}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          )}
                        </Paper>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })()}
          </DialogContent>
        </Dialog>
      </ManagementContainer>
    </>
  );
};

export default ShowTimeManagement;
